'use client';

import React,
{
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import
{
  Box,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
  useMediaQuery,
} from '@mui/material';

import { usePathname, useRouter } from 'next/navigation';
import { colorVariables, miles } from '@/constants';
import { SearchLayoutContext } from '@/page-components/Search/SearchLayout/SearchLayout';
import { ServicesLayoutContext } from '@/page-components/Services/ServicesLayout/ServicesLayout';
import BreadCrumbs from '@/page-components/Search/BreadCrumbs';
import { SearchContext } from '@/page-components/Search';
import { ServiceContext } from '@/page-components/Services';
import { generateSearchMessage } from '@/components/SearchMain/functions';
import { useQuery } from '@/hooks/useQuery';
import theme from '@/theme';
import EmptySearchResult from '@/page-components/Search/EmptySearchResult';

const SearchMain = ({ children }: { children: React.ReactNode }) => {
  const path = usePathname();
  const pathItems = path.split('/');
  const isSearch = path.includes('/hire');
  const { queryParams } = useQuery();
  const route = useRouter();

  const [selectedMiles, setSelectedMiles] = useState(miles.find(mile => mile === queryParams.get('radius')) || miles[0]);
  const [isSelectedMilesVisible, setIsSelectedMilesVisible] = useState(false);

  const {
    location, term,
  } = useContext(SearchLayoutContext);
  const {
    location: serviceLocation,
    term: serviceTerm,
  } = useContext(ServicesLayoutContext);
  const currentContext = useMemo(() => (isSearch ? SearchContext : ServiceContext), [path]);
  const { searchData, searchMeta } = useContext(currentContext);
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

  useEffect(() => {
    const stateIndex = pathItems.findIndex(item => item.length === 2);
    const state = stateIndex !== -1 ? pathItems[stateIndex] : '';
    const city = stateIndex !== -1 ? pathItems[stateIndex + 1] : '';

    if (queryParams.has('loc') || (state?.length === 2 && city?.length)) {
      setIsSelectedMilesVisible(true);
    } else {
      setIsSelectedMilesVisible(false);
    }
  }, [queryParams.get('loc'), path]);

  useEffect(() => {
    if (queryParams.has('radius')) {
      setSelectedMiles(queryParams.get('radius') || miles[0]);
    }
  }, [queryParams.get('radius')]);

  const handleMilesChange = async (e: SelectChangeEvent) => {
    const newRadius = e.target.value;
    setSelectedMiles(newRadius);

    const newSearchParams = new URLSearchParams(queryParams.toString());
    newSearchParams.set('radius', newRadius);
    newSearchParams.delete('page');

    if (serviceTerm) {
      newSearchParams.set('term', serviceTerm);
    }

    if (term) {
      newSearchParams.set('term', term);
    }

    if (isSearch) {
      newSearchParams.delete('page');
      route.push(`/hire?${newSearchParams.toString()}`);
    }

    if (!isSearch) {
      route.push(`/hire?loc=${serviceLocation?.id}&${newSearchParams.toString()}`);
    }
  };

  const message = useMemo<{ title: string, description: string }>(
    () => (isSearch
      ? generateSearchMessage(null, location, term)
      : searchMeta),
    [location?.id, term],
  );

  return (
    <Box
      flex={1}
      display='flex'
      flexDirection='column'
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingX: 1,
          [theme.breakpoints.up('lg')]: {
            paddingX: 16,
          },
        }}
      >
        {searchData?.length ? (
          <Box>
            <Typography
              variant='h6'
              component='h1'
              fontWeight={600}
              paddingTop={3}
              paddingX={3}
            >
              {message.title}
            </Typography>
            <Typography
              variant='body1'
              component='h2'
              fontWeight={400}
              marginTop={1}
              paddingX={3}
            >
              {message.description}
            </Typography>
          </Box>
        ) : (
          <EmptySearchResult />
        )}

        {isSelectedMilesVisible && isDesktop && (
          <Box
            display='flex'
            alignItems='center'
            mr={1}
            mt={1}
            sx={{ gap: '12px' }}
          >
            <Typography
              fontSize={14}
              sx={{ textWrap: 'nowrap' }}
            >
              Results within
            </Typography>
            <Select
              labelId='miles-label'
              id='miles'
              value={selectedMiles}
              onChange={handleMilesChange}
              sx={{
                minWidth: 100,
                fontSize: 14,
                '.MuiSelect-select': {
                  padding: '10px 32px 10px 14px',
                },
              }}
            >
              {miles.map((mile) => (
                <MenuItem
                  key={mile}
                  value={mile}
                  sx={{
                    fontSize: 14,
                  }}
                >
                  {`${mile} miles`}
                </MenuItem>
              ))}
            </Select>
          </Box>
        )}
      </Box>
      {children}
      <BreadCrumbs
        sx={{ padding: 3, borderTop: `1px solid ${colorVariables.separatorColor}` }}
      />
    </Box>
  );
};

export default SearchMain;
